import {
  ButtonLayout,
  colors,
  dimensions,
  Footer,
  StyledFC,
  CaretDownIcon
} from '@bettermarks/bm-ui-components';
import styled, {css} from 'styled-components/macro';
import {ModalDetails} from './modal.sub-components';
import {DetailHeader} from './DetailHeader';
import {AnimatePresence, motion} from 'framer-motion';
import {useState} from 'react';

import '../../themes/datepicker/datepicker.scss';

import {CloseIcon} from '../Icons/Close';

import {CheckBox} from '../Checkbox';

const {SubContent} = ModalDetails;

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  solid,
  regular,
  brands
} from '@fortawesome/fontawesome-svg-core/import.macro';
import {Knob, KnobSize, KnobType, KnobVariant} from '../Knob';
import {CaretIcon, Orientation} from '../Icons';
import {TabButton, Button, Theme, Variant} from '../Button';

export interface ModalProps {
  className?: string;
  modalOpen: boolean;
  modalTitle?: any;
  title: string;
  subtitle?: string;
  closeModal: () => void;
  asDetailTooltip?: boolean;
}

export interface AllocateModalProps {
  className?: string;
  modalOpen: boolean;
  modalTitle?: any;
  closeModal: () => void;
  asDetailTooltip?: boolean;
  lobList: any[];
  createAssignment: () => void;
  onRemoveMiniLob: () => void;
  title: any;
  calendars: any;
  selectedClassAndStudents: any;
  classAndStudents: any;
  disabledButton?: boolean;
}

const StyledFooter = styled(Footer)`
  justify-content: flex-end;
  gap: 12px;
`;

/* Tabs */
interface TabNavItemProps {
  id?: string;
  title: any;
  activeTab?: any;
  setActiveTab?: any;
  icon?: any;
  arrow?: boolean;
}

const _TabNavItem: StyledFC<TabNavItemProps> = ({
  className,
  id,
  icon,
  title,
  activeTab,
  setActiveTab,
  arrow
}) => {
  const handleClick = () => {
    setActiveTab(id);
  };

  return (
    <TabButton
      className={className}
      onClick={handleClick}
      active={activeTab === id}
    >
      {icon}
      {title}
      {arrow && (
        <CaretIcon fill={colors.cGray800} orientation={Orientation.down} />
      )}
    </TabButton>
  );
};

export const TabNavItem = styled(_TabNavItem)`
  display: inline-flex;
  &,
  & path {
    transition: 0.15s ease-out;
  }
`;

const TabPanel: StyledFC<{id: string; activeTab: any; children: any}> = ({
  id,
  activeTab,
  children,
  className
}) => {
  return activeTab === id ? (
    <StyledTabPanel
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      transition={{ease: 'easeInOut', duration: 0.25}}
      className={className}
    >
      {children}
    </StyledTabPanel>
  ) : null;
};

const StyledTabPanel = styled(motion.div)`
  display: flex;
  flex: 1;
`;

const TabNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 ${dimensions.spaceM} ${dimensions.spaceM};
  margin: 0;
  gap: ${dimensions.spaceS};
  li {display: inline-flex;}
}
`;

const TabPanels = styled.div`
  display: flex;
  min-height: 364px;
  height: 405px;
`;

export const Tabs = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
`;
/* Tabs */
const Calendar = styled.div`
  display: flex;
  flex: 1;
  gap: 24px;
  width: 100%;
  & > div {
    display: flex;
    flex: 1;
  }
`;
const StyledDetailHeader = styled(DetailHeader)`
  border-bottom: none;
`;

const RowContainer = styled.div<{padding?: number; scroll?: boolean}>`
  display: flex;
  flex-direction: column;
  flex: 1;

  padding: ${p => (p.padding ? p.padding + 'px' : 0)};
  border-top: 1px solid ${colors.cGray400};
  overflow-y: ${p => (p.scroll ? 'auto' : 'hidden')};
`;

interface ClassListProps {
  id: string;
  name: string;
  activeClass: any;
  setActiveClass: any;
}

const Name = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
`;

const Wrapper = styled.div<{active?: boolean}>`
  transition: background-color 0.25s ease-in-out;
  background-color: ${p => (p.active ? colors.cBlue200 : colors.cWhite)};
  display: flex;
  flex: 1;
  padding: 14px 12px;
  align-items: center;
`;

const RadioInput = styled.span<{active: any}>`
  width: 16px;
  height: 16px;
  display: flex;
  border-radius: 50%;
  transition: all 0.25s ease-in-out;
  border: 2px solid ${p => (p.active ? 'transparent' : colors.cBlue700)};
  background-color: ${p => (p.active ? colors.cBlue700 : colors.cWhite)};
`;

const _ClassListItem: StyledFC<ClassListProps> = ({
  className,
  id,
  name,
  activeClass,
  setActiveClass
}) => {
  const handleClick = () => {
    setActiveClass(id);
  };
  return (
    <li className={className} onClick={handleClick}>
      <Wrapper active={activeClass === id}>
        <RadioInput active={activeClass === id} />
        <Name>{name}</Name>
      </Wrapper>
    </li>
  );
};

export const List = styled.ul`
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  border: 1px solid ${colors.cGray500};
  border-radius: 4px;
  /* max-height: 350px; */
    overflow-y: auto;
    overflow-x: hidden;
}
`;
const ClickArea = styled.div<{active: boolean}>`
  background: ${p => (p.active ? colors.cBlue200 : colors.cWhite)};
  display: flex;
  flex: 1;
  width: 100%;
`;

const _StudentRow: StyledFC<{
  firstName: string;
  lastName: string;
  checked: boolean;
  onCheck: any;
}> = ({className, firstName, lastName, onCheck, checked}) => {
  return (
    <li className={className}>
      <ClickArea active={checked} onClick={onCheck}>
        <CheckBox small checked={checked} padding='12px' />
        <Name>
          {firstName} {lastName}
        </Name>
      </ClickArea>
    </li>
  );
};

export const StudentRow = styled(_StudentRow)`
  border-bottom: 1px solid ${colors.cGray400};
  cursor: pointer;
`;

export const AssignmentButton = styled(Knob)<{disabled?: boolean}>`
  ${p =>
    p.disabled &&
    css`
      cursor: default;
      &,
      &:hover {
        background-color: ${colors.cGray400};
      }
    `};
`;

const _AllocationModal: StyledFC<AllocateModalProps> = ({
  className,
  modalTitle,
  modalOpen,
  lobList,
  title,
  closeModal,
  createAssignment,
  onRemoveMiniLob,
  calendars,
  selectedClassAndStudents,
  classAndStudents,
  disabledButton
}) => {
  const [activeTab, setActiveTab] = useState('tab1');

  return (
    <AnimatePresence>
      {modalOpen && (
        <motion.div
          initial={{opacity: 0, x: 480}}
          animate={{opacity: 1, x: 0}}
          transition={{ease: 'easeOut', duration: 0.1}}
          className={className}
        >
          <StyledDetailHeader
            contentType={false}
            modalTitle={modalTitle}
            onClose={closeModal}
            closeFixed={false}
          />

          <RowContainer scroll>
            <Tabs>
              <TabNav>
                <TabNavItem
                  id='tab1'
                  icon={<FontAwesomeIcon icon={solid('address-book')} />}
                  title={selectedClassAndStudents}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabNavItem
                  id='tab2'
                  icon={<FontAwesomeIcon icon={solid('calendar-days')} />}
                  title={title}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              </TabNav>
              <TabPanels>
                <TabPanel id='tab1' activeTab={activeTab}>
                  <RowContainer padding={16}>{classAndStudents}</RowContainer>
                </TabPanel>
                <TabPanel id='tab2' activeTab={activeTab}>
                  <RowContainer padding={24}>
                    <Calendar>{calendars}</Calendar>
                  </RowContainer>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </RowContainer>
          <StyledFooter buttonLayout={ButtonLayout.row}>
            <Button
              onClick={closeModal}
              buttonTheme={Theme.neutral}
              variant={Variant.ghost}
            >
              Schließen
            </Button>

            <AssignmentButton
              label={
                lobList.length > 1
                  ? lobList.length + ' Lernziele zuweisen'
                  : lobList.length + ' Lernziel zuweisen'
              }
              type={KnobType.button}
              size={KnobSize.medium}
              variant={KnobVariant.primary}
              onClick={!disabledButton ? createAssignment : null}
              disabled={disabledButton}
              maxHeight={40}
            />
          </StyledFooter>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CUSTOM_POSITION = css`
  right: 0;
  top: 120px;
  bottom: 56px;
`;

export const AllocationModal = styled(_AllocationModal)<
  Pick<ModalProps, 'asDetailTooltip'>
>`
  display: flex;
  flex-direction: column;
  border-radius: ${p => (p.asDetailTooltip ? 0 : '6px')};
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.16);
  background: ${colors.cWhite};
  transition: all 0.5s ease-in-out;
  position: fixed;
  z-index: 1;

  ${StyledDetailHeader} + ${RowContainer} {
    border: none;
  }

  ${SubContent} {
    margin-top: 15px;
  }

  ${Footer} {
    padding: ${dimensions.spaceS};
    border-top: 1px solid ${colors.cGray300};
    margin: 0;
  }
  @media (max-height: 600px) {
    max-height: 90vh;
  }

  @media (min-width: 768px) {
    width: 640px;
    ${p =>
      p.asDetailTooltip
        ? CUSTOM_POSITION
        : css`
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) !important;
          `};
  }
  @media (max-width: 767px) {
    min-width: 100%;
    min-height: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  ${p =>
    p.modalOpen
      ? css`
          opacity: 1;
          visibility: visible;
          z-index: 10;
        `
      : css`
          background-color: #fff;
          opacity: 0;
          visibility: hidden;
        `};
  [class^='AllocationModal__StudentRow']:last-child {
    border: none;
  }
`;
